/**
 * CSV Export utility for dashboard tables.
 *
 * Extracts data from a TableData structure, handling colspan/rowspan
 * in headers, and produces a CSV string suitable for download.
 */

import type { TableData, TableHeader } from "./reportGenerator";

/** Escape a CSV cell value (wrap in quotes if it contains commas, quotes, or newlines). */
function escapeCell(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Expand multi-row headers with colspan/rowspan into a flat 2D grid.
 *
 * Each cell in the grid contains the header label. Cells spanned by
 * colspan or rowspan are filled with the spanning cell's label.
 */
function expandHeaders(headers: TableHeader[][]): string[][] {
  if (headers.length === 0) return [];

  // Determine total column count from the first row
  const totalCols = headers[0].reduce((sum, h) => sum + (h.colspan ?? 1), 0);
  const totalRows = headers.length;

  // Create empty grid
  const grid: (string | null)[][] = Array.from({ length: totalRows }, () =>
    Array(totalCols).fill(null)
  );

  // Fill grid respecting colspan and rowspan
  for (let ri = 0; ri < totalRows; ri++) {
    let ci = 0;
    for (const header of headers[ri]) {
      // Find next empty cell in this row
      while (ci < totalCols && grid[ri][ci] !== null) {
        ci++;
      }
      if (ci >= totalCols) break;

      const colspan = header.colspan ?? 1;
      const rowspan = header.rowspan ?? 1;

      for (let dr = 0; dr < rowspan && ri + dr < totalRows; dr++) {
        for (let dc = 0; dc < colspan && ci + dc < totalCols; dc++) {
          grid[ri + dr][ci + dc] = header.label;
        }
      }
      ci += colspan;
    }
  }

  // Replace any remaining nulls with empty string
  return grid.map((row) => row.map((cell) => cell ?? ""));
}

/**
 * Export a TableData structure to a CSV string.
 * If comparisonTable is provided, interleaves comparison rows after each primary row.
 */
export function exportTableDataToCsv(
  tableData: TableData,
  comparisonTable?: TableData
): string {
  const lines: string[] = [];

  // Expand headers into flat rows
  const headerGrid = expandHeaders(tableData.headers);
  for (const headerRow of headerGrid) {
    lines.push(headerRow.map(escapeCell).join(","));
  }

  // Data rows
  for (let i = 0; i < tableData.rows.length; i++) {
    const row = tableData.rows[i];
    const cells = [row.label, ...row.cells.map((c) => c.value)];
    lines.push(cells.map(escapeCell).join(","));

    if (comparisonTable) {
      const cmpRow = comparisonTable.rows[i];
      if (cmpRow) {
        const cmpCells = [
          `${cmpRow.label} (Comparison)`,
          ...cmpRow.cells.map((c) => c.value),
        ];
        lines.push(cmpCells.map(escapeCell).join(","));
      }
    }
  }

  return lines.join("\n");
}
